/**
 * THE SHARED OAUTH LANDING -- both providers land here (Task 11: Google, Task
 * 12: eBay), which is what stops the two views from disagreeing about the one
 * screen that matters.
 *
 * IT DECIDES NOTHING ITSELF. The landing classification, the outcome and the
 * screen all come from lib/oauthLanding.ts, where they are pure and driven
 * exhaustively by a Node test. What is left here is an effect and some JSX --
 * deliberately, because "did the security screen degrade into a generic
 * error?" must be a question a test can ask without a renderer.
 *
 * THE FOUR CLAUSES OF ITS CONTRACT:
 *
 *  1. REVIEW POINT B2 -- `clearSearch()` runs BEFORE the finish request, as
 *     the first statement of `runLandingFlow`, in the same effect that starts
 *     it. Not "eventually": `?state=<jti>` must never enter the history stack,
 *     must not survive a reload, and must not ride in a `Referer`.
 *  2. THE FINISH RUNS EXACTLY ONCE. The parked code is single-use and React 18
 *     StrictMode double-invokes effects in development, so the call is
 *     ref-guarded -- a second invocation would answer `invalid_state` to the
 *     user's own legitimate connect and put a security screen in front of
 *     somebody who did nothing wrong.
 *  3. THE LANDING IS FROZEN AT MOUNT. `clearSearch` empties the router's query
 *     immediately, so a component that re-derived its landing from the live
 *     `search` prop would classify `none` one render later. The classification
 *     happens once, in `useState`'s initialiser, and the first paint is already
 *     truthful (`initialOutcome`) rather than a frame of nothing.
 *
 *     THE RELATIONSHIP BETWEEN CLAUSE 2 AND CLAUSE 3 IS ASYMMETRIC, and getting
 *     it backwards is how the load-bearing half gets deleted (review MIN-4).
 *     The ref guard in clause 2 SUBSUMES this freeze: it stops any second run
 *     of the effect, so a re-classified `none` could never reach
 *     `runLandingFlow` and could never erase the screen. The freeze does NOT
 *     subsume the guard -- remove the guard and StrictMode still double-invokes
 *     the effect and burns the single-use state, which a DOM test catches. So:
 *     the guard is the defence, the freeze is cheap redundancy that stops the
 *     effect re-firing on every render and becomes load-bearing again the
 *     moment anyone makes the guard landing-scoped or resets it for a retry.
 *     Removing the freeze alone is behaviour-preserving today; removing the
 *     guard is not. They are not interchangeable.
 *  4. SUCCESS IS A HANDOVER, NOT A VERDICT. `children` is a render prop called
 *     only after a successful finish; that is where Task 11's Google identity
 *     read and Task 12's eBay confirmation gate live. This component never
 *     says "verbunden" on its own.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  classifyLanding, connectScreen, initialOutcome, runLandingFlow,
  type ConnectScreen, type Outcome, type Provider,
} from '../lib/oauthLanding';

export interface ConnectOutcomeProps<R> {
  provider: Provider;
  /** The landing query. Read ONCE, at mount (clause 3). */
  search: URLSearchParams;
  /** POST /admin-api/<provider>/oauth-finish { state }. */
  onFinish: (state: string) => Promise<R>;
  /** Task 2's `clearSearch` (Router.tsx). Injected rather than reached for, so
   *  the ordering in clause 1 is assertable. */
  clearSearch: () => void;
  /** "Erneut versuchen" on the two calm outcomes. Absent means the button
   *  still renders (the operator should see the offer) and does nothing. */
  onRetry?: () => void;
  children: (result: R) => ReactNode;
}

export function ConnectOutcome<R>({
  provider, search, onFinish, clearSearch, onRetry, children,
}: ConnectOutcomeProps<R>) {
  // Frozen at mount (clause 3). `useState`'s initialiser, not `useMemo`: a
  // memo keyed on `search` would recompute the moment clearSearch empties it.
  const [landing] = useState(() => classifyLanding(search));
  const [outcome, setOutcome] = useState<Outcome<R>>(() => initialOutcome<R>(landing));

  // Read through refs, the same reason components/Turnstile.tsx does it: both
  // props are arrow functions rebuilt on every render of the parent view, and
  // neither may be a reason to run this effect a second time.
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;
  const clearSearchRef = useRef(clearSearch);
  clearSearchRef.current = clearSearch;
  const startedRef = useRef(false);

  useEffect(() => {
    // Clause 2, and it is the FIRST thing the effect does: StrictMode's second
    // invocation must not reach `runLandingFlow` at all.
    if (startedRef.current) return;
    startedRef.current = true;
    void runLandingFlow<R>(landing, {
      clearSearch: () => clearSearchRef.current(),
      finish: (state) => onFinishRef.current(state),
      setOutcome,
    });
  }, [landing]);

  if (outcome.kind === 'success') return <>{children(outcome.result)}</>;
  const screen = connectScreen(outcome, provider);
  if (screen === null) return null;
  return <ConnectScreenView screen={screen} onRetry={onRetry} />;
}

/** Renders the record lib/oauthLanding.ts decided. It reads no state and makes
 *  no choice of its own beyond "is this field present". */
function ConnectScreenView({ screen, onRetry }: { screen: ConnectScreen; onRetry?: () => void }) {
  return (
    <section className="connect-outcome" data-tone={screen.tone} role={screen.role}>
      <h2>{screen.title}</h2>
      <p>{screen.body}</p>
      {screen.revocation === null ? null : (
        <p className="connect-outcome-revoke">
          <a href={screen.revocation.href} target="_blank" rel="noopener noreferrer">
            {screen.revocation.label}
          </a>
          {screen.revocation.hint === null ? null : (
            <span className="connect-outcome-revoke-hint">{screen.revocation.hint}</span>
          )}
        </p>
      )}
      {screen.supportNote === null ? null : <p className="connect-outcome-support">{screen.supportNote}</p>}
      {screen.retry === null ? null : (
        <button type="button" onClick={onRetry}>{screen.retry}</button>
      )}
    </section>
  );
}
